/**
 * useGenerateMore
 *
 * Reusable hook for on-demand "Find More" generation on matter tabs.
 * Calls the generate-more endpoint, connects to PubSub for real-time progress,
 * exposes progress state for in-tab rendering.
 * No cooldown - buttons are simply hidden while generating.
 *
 * **SessionStorage persistence**: active operations are persisted to
 * sessionStorage so that when a user switches tabs (which unmounts the
 * component) and comes back, the hook can reconnect to the PubSub group
 * and resume showing progress. Operations expire after MAX_AGE_MS.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../store';
import { generateMoreForStep } from '../store/actions/Matters/MatterActions';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import { getMultiHubSocketService } from '../utils/MultiHubSocketService';
import { useTranslation } from 'react-i18next';
import { toErrorText } from '../utils/errorMessage';
import { useLicenseGate } from './useLicenseGate';

const HUB = 'ai_processing';
const MAX_AGE_MS = 120_000; // 2 minutes - matches the fallback timeout

export type GenerateMoreStep = 'questions' | 'notes' | 'tasks' | 'laws' | 'cases' | 'description' | 'preparatory_works' | 'regulations' | 'agency_decisions';

interface UseGenerateMoreOptions {
  matterUuid: string;
  targetStep: GenerateMoreStep;
  onComplete: () => void;
}

export interface GenerateMoreProgress {
  message: string;
  percent: number;
  status: 'idle' | 'running' | 'complete' | 'failed';
}

/* ── SessionStorage helpers ────────────────────────────────── */

interface PersistedOperation {
  correlationId: string;
  group: string;
  startedAt: number; // Date.now()
  matterUuid: string;
  targetStep: GenerateMoreStep;
}

function storageKey(matterUuid: string, step: GenerateMoreStep) {
  return `generate_more_active_${matterUuid}_${step}`;
}

function persistOperation(op: PersistedOperation) {
  try {
    sessionStorage.setItem(storageKey(op.matterUuid, op.targetStep), JSON.stringify(op));
  } catch { /* quota exceeded - non-critical */ }
}

function loadPersistedOperation(matterUuid: string, step: GenerateMoreStep): PersistedOperation | null {
  try {
    const raw = sessionStorage.getItem(storageKey(matterUuid, step));
    if (!raw) return null;
    const op: PersistedOperation = JSON.parse(raw);
    // Expire stale entries
    if (Date.now() - op.startedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(storageKey(matterUuid, step));
      return null;
    }
    return op;
  } catch {
    return null;
  }
}

function clearPersistedOperation(matterUuid: string, step: GenerateMoreStep) {
  try {
    sessionStorage.removeItem(storageKey(matterUuid, step));
  } catch { /* ignore */ }
}

/* ── Hook ──────────────────────────────────────────────────── */

export function useGenerateMore({ matterUuid, targetStep, onComplete }: UseGenerateMoreOptions) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  /* Generate-more is matter AI (`matter_ai`). On a plan without it (Starter)
     the click raises the upgrade modal instead of a request the server would
     refuse, and `aiLocked` lets a button show it is locked. */
  const { hasMatterAiAccess, checkAiFeatureGate } = useLicenseGate();
  const aiLocked = !hasMatterAiAccess();

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<GenerateMoreProgress>({
    message: '',
    percent: 0,
    status: 'idle',
  });

  const groupRef = useRef<string | null>(null);
  const handlerRef = useRef<((raw: any) => void) | null>(null);
  const mountedRef = useRef(true);
  const generatingRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  /* ── PubSub cleanup (leave group + remove handler) ── */
  const cleanup = useCallback(() => {
    const multiHubService = getMultiHubSocketService();
    if (groupRef.current) {
      multiHubService.emit(HUB, 'leave', { group: groupRef.current });
      groupRef.current = null;
    }
    if (handlerRef.current) {
      multiHubService.off(HUB, '*', handlerRef.current);
      handlerRef.current = null;
    }
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  /* ── Finish helpers (clear persistence + reset state) ── */
  const finishSuccess = useCallback(() => {
    clearPersistedOperation(matterUuid, targetStep);
    cleanup();
    generatingRef.current = false;
    setIsGenerating(false);
    setProgress({ message: '', percent: 0, status: 'idle' });
    onCompleteRef.current();
  }, [matterUuid, targetStep, cleanup]);

  const finishFailure = useCallback(() => {
    clearPersistedOperation(matterUuid, targetStep);
    cleanup();
    generatingRef.current = false;
    setIsGenerating(false);
    setProgress({ message: '', percent: 0, status: 'idle' });
  }, [matterUuid, targetStep, cleanup]);

  /* ── PubSub message handler ── */
  const handlePubSubMessage = useCallback((raw: any) => {
    if (!mountedRef.current) return;

    const event = Array.isArray(raw) ? raw[0] : raw || {};
    const innerData = event.data || {};
    const data = { ...event, ...innerData };

    const subType = data.sub_type || data.event_name || '';
    const message = data.message || '';
    const progressPercent = data.progress_percent || 0;

    // ── Step started ──
    if (subType === 'step_started') {
      setProgress({
        message: message || t('Starting...'),
        percent: progressPercent || 10,
        status: 'running',
      });
      return;
    }

    // ── Step progress ──
    if (subType === 'step_progress') {
      setProgress(prev => ({
        ...prev,
        message: message || prev.message,
        percent: progressPercent || prev.percent,
        status: 'running',
      }));
      return;
    }

    // ── Completion ──
    if (subType === 'generate_more_complete') {
      const resultCount = data.result_count ?? 0;
      const stepLabel = data.target_step || targetStep;

      setProgress({
        message: t('Found {{count}} {{step}}', { count: resultCount, step: t(stepLabel) }),
        percent: 100,
        status: 'complete',
      });

      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Found {{count}} {{step}}', { count: resultCount, step: t(stepLabel) }),
        modalType: 'success',
        duration: 3000,
      }));

      // Short delay so user sees the 100% state, then clean up and refresh
      setTimeout(() => {
        if (!mountedRef.current) return;
        finishSuccess();
      }, 1500);

      return;
    }

    // ── Failure ──
    if (subType === 'generate_more_failed') {
      const errorMsg = toErrorText(data.error) || toErrorText(data.message) || t('Generation failed');

      setProgress({
        message: errorMsg,
        percent: 0,
        status: 'failed',
      });

      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMsg,
        modalType: 'error',
        duration: 5000,
      }));

      setTimeout(() => {
        if (!mountedRef.current) return;
        finishFailure();
      }, 3000);

      return;
    }
  }, [dispatch, t, targetStep, finishSuccess, finishFailure]);

  useEffect(() => {
    handlerRef.current = handlePubSubMessage;
  }, [handlePubSubMessage]);

  /* ── Connect to PubSub group and start listening ── */
  const connectToPubSub = useCallback(async (group: string) => {
    const multiHubService = getMultiHubSocketService();
    try {
      await multiHubService.connectToHub(HUB);
      multiHubService.emit(HUB, 'join', { group });
      multiHubService.on(HUB, '*', handlePubSubMessage);
      groupRef.current = group;
    } catch {
      // PubSub connection failed - show indeterminate progress
      setProgress({
        message: t('Processing (live updates unavailable)...'),
        percent: 50,
        status: 'running',
      });
    }
  }, [handlePubSubMessage, t]);

  /* ── Start the fallback timer from a given start time ── */
  const startFallbackTimer = useCallback((startedAt: number) => {
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, MAX_AGE_MS - elapsed);
    fallbackTimerRef.current = setTimeout(() => {
      if (!mountedRef.current || !generatingRef.current) return;
      finishSuccess(); // Treat as silent success - refresh data
    }, remaining);
  }, [finishSuccess]);

  /* ── On mount: check for a persisted operation and reconnect ── */
  useEffect(() => {
    mountedRef.current = true;

    const persisted = loadPersistedOperation(matterUuid, targetStep);
    if (persisted) {
      // Resume: show progress and reconnect to PubSub
      generatingRef.current = true;
      setIsGenerating(true);

      const elapsed = Date.now() - persisted.startedAt;
      const estimatedPercent = Math.min(90, Math.round((elapsed / MAX_AGE_MS) * 100));

      setProgress({
        message: t('Resuming progress...'),
        percent: estimatedPercent,
        status: 'running',
      });

      connectToPubSub(persisted.group);
      startFallbackTimer(persisted.startedAt);
    }

    return () => {
      mountedRef.current = false;
      // On unmount: leave PubSub but do NOT clear sessionStorage
      // so a re-mount can pick it back up
      const multiHubService = getMultiHubSocketService();
      if (groupRef.current) {
        multiHubService.emit(HUB, 'leave', { group: groupRef.current });
        groupRef.current = null;
      }
      if (handlerRef.current) {
        multiHubService.off(HUB, '*', handlerRef.current);
        handlerRef.current = null;
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [matterUuid, targetStep]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Trigger a new generation ── */
  const handleGenerateMore = useCallback(async (selectedIndexesOrEvent?: string[] | React.MouseEvent) => {
    const selectedIndexes = Array.isArray(selectedIndexesOrEvent) ? selectedIndexesOrEvent : undefined;
    if (generatingRef.current || !matterUuid) return;
    if (aiLocked) {
      checkAiFeatureGate('matter_ai');
      return;
    }

    generatingRef.current = true;
    setIsGenerating(true);
    setProgress({
      message: t('Starting request...'),
      percent: 5,
      status: 'running',
    });

    try {
      const result = await dispatch(generateMoreForStep(matterUuid, targetStep, selectedIndexes));
      if (!result || !result.correlationId) {
        generatingRef.current = false;
        setIsGenerating(false);
        setProgress({ message: '', percent: 0, status: 'idle' });
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Failed to start generation'),
          modalType: 'error',
          duration: 3000,
        }));
        return;
      }

      // Persist operation before connecting to PubSub
      const correlationId = result.correlationId;
      const group = `generate_more_${correlationId}`;
      const now = Date.now();

      persistOperation({
        correlationId,
        group,
        startedAt: now,
        matterUuid,
        targetStep,
      });

      await connectToPubSub(group);
      startFallbackTimer(now);
    } catch {
      generatingRef.current = false;
      setIsGenerating(false);
      setProgress({ message: '', percent: 0, status: 'idle' });
      clearPersistedOperation(matterUuid, targetStep);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to start generation'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  }, [matterUuid, targetStep, dispatch, connectToPubSub, startFallbackTimer, t, aiLocked, checkAiFeatureGate]);

  return {
    isGenerating,
    progress,
    handleGenerateMore,
    aiLocked,
  };
}
